import React, { useState, useRef, useCallback } from 'react';
import { FileText, Eye, Download, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getMeetingContractDocx } from '../../services/Calendar';
import { renderAsync } from 'docx-preview';
import { useI18n } from '../../i18n';

interface ContractDocumentCardProps {
  meetingId: string;
  documentType?: string;
}

const ContractDocumentCard: React.FC<ContractDocumentCardProps> = ({
  meetingId,
  documentType: documentTypeProp,
}) => {
  const { t } = useI18n();
  const c = t.meetings.contractDocument;
  const documentType = documentTypeProp || c.defaultType;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const loadPreview = useCallback(async () => {
    if (!containerRef.current) return;

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const response = await getMeetingContractDocx(meetingId);

      if (!response.success || !response.data) {
        throw new Error('Failed to load contract document');
      }

      // Clear previous content
      containerRef.current.innerHTML = '';

      // Render the DOCX using docx-preview
      await renderAsync(response.data, containerRef.current, undefined, {
        className: 'docx-preview',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        useBase64URL: true,
      });
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewError(c.previewFailed);
    } finally {
      setPreviewLoading(false);
    }
  }, [meetingId, c.previewFailed]);

  const handleOpenPreview = () => {
    setPreviewOpen(true);
  };

  const handlePreviewOpenChange = (open: boolean) => {
    setPreviewOpen(open);
    if (!open) {
      setPreviewError(null);
    }
  };

  // Load preview when dialog opens
  React.useEffect(() => {
    if (previewOpen) {
      // Small delay to ensure the dialog is mounted
      const timer = setTimeout(() => {
        loadPreview();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [previewOpen, loadPreview]);

  const handleDownload = async () => {
    setDownloadLoading(true);

    try {
      // Generate filename from document type
      const filename = `${(documentTypeProp || 'Contract Document').toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')}.docx`;

      // Fetch the blob through our backend
      const blobResponse = await getMeetingContractDocx(meetingId);

      if (blobResponse.success && blobResponse.data) {
        // Create a download link with the proper filename
        const url = window.URL.createObjectURL(blobResponse.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Failed to download contract');
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {documentType}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {c.generated(documentType)}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleOpenPreview}
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-2" />
              {c.preview}
            </Button>
            <Button
              variant="default"
              onClick={handleDownload}
              disabled={downloadLoading}
              className="flex-1"
            >
              {downloadLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {c.downloading}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  {c.download}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={handlePreviewOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {c.previewTitle(documentType)}
            </DialogTitle>
            <DialogDescription>
              {c.previewDescription(documentType)}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto min-h-[500px] border rounded-md bg-white">
            {previewLoading && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {c.loadingPreview}
                  </p>
                </div>
              </div>
            )}

            {previewError && (
              <div className="p-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{previewError}</AlertDescription>
                </Alert>
              </div>
            )}

            <div
              ref={containerRef}
              className={previewLoading || previewError ? 'hidden' : ''}
              style={{ minHeight: '500px' }}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
            >
              {c.close}
            </Button>
            <Button
              onClick={handleDownload}
              disabled={downloadLoading}
            >
              {downloadLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {c.downloading}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  {c.download}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContractDocumentCard;
